// frontend/src/api/valuationPayment.ts
//
// VAL-STRIPE-GATE-1 - the owner pays before "Your value". The server refuses every
// value route with 402 until a Stripe payment (or an admin override) is recorded;
// these two calls drive the pay screen. The card is typed on Stripe's own page.
import { apiClient } from '@/api/client'

export interface PaymentOption {
  tier: string
  label: string
  /** VAL-TIERS-1-2: the pay screen's tier name and one plain line (server wording). */
  title?: string
  description?: string
  amount_cents: number
  currency: string
}

/** VAL-FIX-PAY-BUGS-1: why the value cannot be worked out yet, and which owner step fixes it.
 *  The server sends no words here - the screen's own plain text is shown. */
export interface ValueBlocker {
  /** An owner step key (_steps.ts): company | financials | adjust | intake. */
  fix_step: string | null
  fix_reason: string
  unanswered_question_ids: string[]
}

export interface PaymentInfo {
  valuation_engagement_id: string
  paid: boolean
  payments_configured: boolean
  options: PaymentOption[]
  /** VAL-FIX-PAY-BUGS-1 P1: false = the value cannot be computed yet; no payment is taken. */
  ready?: boolean
  blocker?: ValueBlocker | null
  /** VAL-FIX-PAY-BUGS-1 bug 3: null while unpaid. paid_at is UTC ISO. */
  paid_at?: string | null
  price_key?: string | null
  amount_cents?: number | null
  currency?: string | null
  ordered_by_role?: string | null
  stripe_session_id?: string | null
  /** VAL-68-OWNER-RULINGS R2: the higher options not yet bought (empty while unpaid). */
  upgrades?: PaymentOption[]
}

const eng = (id: string) => `/v1/valuation/engagements/${id}`

export async function fetchPaymentInfo(id: string): Promise<PaymentInfo> {
  const { data } = await apiClient.get<PaymentInfo>(`${eng(id)}/payment`)
  return data
}

/** The hosted Stripe Checkout URL to send the browser to. */
export async function startCheckout(id: string, tier: string): Promise<string> {
  const { data } = await apiClient.post<{ url: string }>(`${eng(id)}/checkout`, { tier })
  return data.url
}

/** True when the server refused because the value is not paid for yet. */
export function isPaymentRequired(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 402
}

/** VAL-FIX-PAY-BUGS-1 bug 2: the step a value refusal names (`details.fix_step`), or null.
 *  A 422 (a figure the engine cannot use) is fixed on Financials. */
export function valueBlockerFromError(err: unknown): ValueBlocker | null {
  const res = (err as { response?: { status?: number; data?: { error?: { details?: Record<string, unknown> } } } })
    ?.response
  const details = res?.data?.error?.details
  if (details && typeof details.fix_step === 'string') {
    const ids = Array.isArray(details.unanswered_question_ids) ? details.unanswered_question_ids : []
    return {
      fix_step: details.fix_step,
      fix_reason: typeof details.fix_reason === 'string' ? details.fix_reason : 'inputs',
      unanswered_question_ids: ids.filter((id): id is string => typeof id === 'string'),
    }
  }
  if (res?.status === 422) return { fix_step: 'financials', fix_reason: 'figures', unanswered_question_ids: [] }
  return null
}
